import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, HelpCircle, Home, Book, Code, Users, FileText, Archive, GitBranch } from 'lucide-react';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const FAQ: React.FC = () => {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const faqs: FAQItem[] = [
    {
      category: 'About Wisetree AI',
      question: 'What is Wisetree AI?',
      answer: 'A storytelling studio for serious creators. You bring the idea; an AI production crew — director, writer, photographer, composer, editor, and a media-legal advisor — takes it from a story spine to a finished micro-drama or ad video you own. Every AI tool makes clips; we help you finish the whole story.'
    },
    {
      category: 'About Wisetree AI',
      question: 'Who is it for?',
      answer: 'Creators who want to complete real work — micro-drama episode series, ad videos, explainers — not just generate one-off clips. If you have a story to tell and want a team that supplies the craft, this is for you.'
    },
    {
      category: 'About Wisetree AI',
      question: 'How is this different from other AI video tools?',
      answer: 'Most tools generate a single clip and stop. Wisetree gives you a persistent crew that knows your story, keeps characters and tone consistent across a whole series, and guides you idea → beat sheet → shots → assembled video — a studio team, not a prompt box.'
    },
    {
      category: 'About Wisetree AI',
      question: 'Do I need to know filmmaking?',
      answer: 'No. The crew supplies the craft. You direct in plain language with guided, editable shot cards — camera, look, and pacing are handled or offered as simple choices. No timelines, no node graphs, no jargon.'
    },
    {
      category: 'About Wisetree AI',
      question: 'Can I pick my own crew?',
      answer: 'Yes. Each purpose starts with a sensible default crew you can use in one tap, or customize — swap a director style, add a composer, bring in the media-legal advisor. The team is yours to tune.'
    },
    {
      category: 'Creating',
      question: 'What can I make right now?',
      answer: 'Video stories (micro-drama series, music videos, explainers) and ad videos. You get a guided flow: describe the idea, the crew drafts a beat sheet, you shape the shots, and it renders and assembles into a finished video.'
    },
    {
      category: 'Creating',
      question: 'How do characters stay consistent across episodes?',
      answer: 'Character lock. Your cast is anchored so the same face, look, and tone carry across shots and episodes — continuity that survives a whole series, not just one 15-second shot.'
    },
    {
      category: 'Credits',
      question: 'What does it cost?',
      answer: 'Your first storyboard is on us: the crew writes your story and draws its opening shots before you spend anything. After that you buy credits by top-up, starting with a one-time $10 starter pack sized to animate and publish that first episode. Credits never expire, and you\'re only charged when a creation succeeds. The Wisetree Plus plan is a licence to sell your episodes; it adds no credits.'
    },
    {
      category: 'Credits',
      question: 'Which AI models does it use?',
      answer: 'We route to strong, current models automatically and pick the right one per task — so you choose the outcome (a video, an image, a track), never a model name.'
    },
    {
      category: 'Owning & Selling',
      question: 'Do I own what I make?',
      answer: 'What you create is yours to use, keep, and sell, to the fullest extent the law allows. (Note: in the US, purely AI-generated output may not be eligible for copyright — we\'re honest about that and give you clear ownership terms plus provenance rather than a promise the law can\'t back. See our Terms.)'
    },
    {
      category: 'Owning & Selling',
      question: 'Can I sell my videos and photobooks?',
      answer: 'That\'s the plan. You\'ll be able to list finished work on the Wisetree marketplace, priced in credits; buyers unlock it and you earn. Wisetree keeps a 20% platform cut (a 10% copyright royalty + 10% commission) and you keep 80%, paid out to your bank. Selling launches in phases — creation and ownership come first.'
    },
    {
      category: 'Owning & Selling',
      question: 'How do payouts work?',
      answer: 'Your marketplace earnings accrue as a balance and are paid to your bank via our payments partner (Stripe), which handles identity and tax details. Payouts roll out with the marketplace.'
    },
    {
      category: 'Your Content & Privacy',
      question: 'Is my work private?',
      answer: 'Yes. Your projects are private to you unless you choose to share or list them, and you can export anything any time — no lock-in. Per-chat storage choices, including your own Google Drive, are supported.'
    },
    {
      category: 'Your Content & Privacy',
      question: 'Can it make images of real people?',
      answer: 'Wisetree is for original creation. We don\'t allow content that impersonates real people or any intimate or non-consensual content, and you\'re responsible for having the rights to everything you create and sell, per our Terms.'
    },
    {
      category: 'Getting Started',
      question: 'How do I start?',
      answer: 'On the homepage, pick what you\'re making, sign in with Google, and you land in your studio with the crew assembled — ready for your first shot.'
    },
    {
      category: 'Getting Started',
      question: 'How do I get help?',
      answer: 'Email support@wisetree.ai with your account email and a short description, and we\'ll get back to you.'
    }
  ];

  const categories = Array.from(new Set(faqs.map(faq => faq.category)));

  // Per-route meta + FAQPage structured data — lets Google + AI answer engines (GEO/AEO)
  // surface these Q&As and gives the page its own title/description/canonical.
  useDocumentMeta({
    title: 'FAQ — Wisetree AI',
    description: 'Answers about Wisetree AI: how the group-chat team of specialists works, AI credits, privacy, exports, and getting started.',
    canonical: 'https://wisetree.ai/faq',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    },
  });

  const toggleFAQ = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Getting Started':
        return <Home className="h-6 w-6 text-primary-400" />;
      case 'Task Management':
        return <Archive className="h-6 w-6 text-primary-400" />;
      case 'Documentation':
        return <Book className="h-6 w-6 text-primary-400" />;
      case 'Code Repositories':
        return <Code className="h-6 w-6 text-primary-400" />;
      case 'Team Collaboration':
        return <Users className="h-6 w-6 text-primary-400" />;
      case 'CRM & Contacts':
        return <FileText className="h-6 w-6 text-primary-400" />;
      case 'Files & Storage':
        return <FileText className="h-6 w-6 text-primary-400" />;
      case 'Best Practices':
        return <GitBranch className="h-6 w-6 text-primary-400" />;
      default:
        return <Book className="h-6 w-6 text-primary-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-black text-secondary-900 dark:text-white">
      {/* Header */}
      <header className="border-b border-secondary-200 dark:border-secondary-800">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white transition-colors">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">Frequently Asked Questions</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="prose prose-invert max-w-none">
          {/* Introduction */}
          <section className="mb-12">
            <div className="bg-secondary-100 dark:bg-secondary-900 rounded-lg p-6 mb-8">
              <div className="flex items-start gap-4">
                <HelpCircle className="h-6 w-6 text-primary-400 mt-1" />
                <div>
                  <h2 className="text-xl font-semibold mb-2 text-secondary-900 dark:text-white">Questions?</h2>
                  <p className="text-secondary-700 dark:text-secondary-300">
                    What Wisetree AI is, how the specialist agents work, and how access and your data are handled.
                    Expand any question to see the answer.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Categories */}
          {categories.map((category) => (
            <section key={category} className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-secondary-900 dark:text-white flex items-center gap-2">
                {getCategoryIcon(category)}
                {category}
              </h2>

              <div className="space-y-4">
                {faqs
                  .filter((faq) => faq.category === category)
                  .map((faq) => {
                    const faqId = faqs.indexOf(faq);
                    const isExpanded = expandedId === faqId;

                    return (
                      <div
                        key={faqId}
                        className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg overflow-hidden"
                      >
                        <button
                          onClick={() => toggleFAQ(faqId)}
                          className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-secondary-200/50 dark:hover:bg-secondary-800/50 transition-colors"
                        >
                          <span className="font-semibold text-secondary-900 dark:text-white pr-4">
                            {faq.question}
                          </span>
                          {isExpanded ? (
                            <ChevronUp className="h-5 w-5 text-secondary-500 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-secondary-500 flex-shrink-0" />
                          )}
                        </button>

                        {isExpanded && (
                          <div className="px-6 pb-4 text-secondary-700 dark:text-secondary-300 whitespace-pre-line">
                            {faq.answer}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </section>
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-secondary-200 dark:border-secondary-800 mt-20">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-sm text-secondary-600 dark:text-secondary-400">
              © 2026 Wisetree AI Pty Ltd. All rights reserved.
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/" className="text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white transition-colors">
                Home
              </Link>
              <Link to="/privacy" className="text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white transition-colors">
                Terms & Conditions
              </Link>
              <Link to="/dashboard" className="text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white transition-colors">
                Dashboard
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FAQ;
